'use client'
import { type FC, type MouseEvent, memo, useEffect, useState } from 'react'
import { useParams } from 'next/navigation'
import { motion, AnimatePresence } from 'motion/react'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import { getIcon, LOCALE, type TypeAppIcon } from '@/utils'
import type { TypeLang } from '@/schema'
import { mq } from '@/styles'
import { Img, SvgClose } from '.'

type TypeToast = 'info' | 'success' | 'warning' | 'error'
export interface iToastProps {
  type: TypeToast
  title?: string
  message: string
  closeNeeded?: boolean
  icon?: TypeAppIcon
  onClose?: (e?: MouseEvent<HTMLButtonElement>) => void
  timeout?: number
}


const getBgColorByType = (type: TypeToast) => {
  switch (type) {
    case 'info': return 'var(--color-block-background--light)'
    case 'success': return 'var(--color-cms-blue)'
    case 'warning': return 'var(--color-warning)'
    case 'error': return 'var(--color-cms-pink)'
    default: return 'var(--color-block-background--light)'
  }
}

const getTextColorByType = (type: TypeToast) => {
  switch (type) {
    case 'info': return 'var(--color-text--light)'
    case 'success': return 'var(--color-text--dark)'
    case 'warning': return 'var(--color-text--light)'
    case 'error': return 'var(--color-text)'
    default: return 'var(--color-text--light)'
  }
}

export const Toast: FC<iToastProps> = memo(({ type, title, message, icon, timeout = 5000, closeNeeded = false, onClose }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const [visible, setVisible] = useState(true)
  const { t } = useTranslator()

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (!closeNeeded) {
      timer = setTimeout(() => {
        setVisible(false)
      }, timeout)
    }

    return () => { clearTimeout(timer) }
  }, [timeout, closeNeeded])

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation() // Prevent the event from reaching the ToastContainer
    setVisible(false)
    if (onClose) onClose()
  }

  // role='alert'
  return (
    <AnimatePresence mode='wait'>
      {visible &&
        <DivToastItem $type={type} role={(type === 'error' || type === 'warning') ? 'alert' : 'status'} variants={variants} initial='initial' animate='animate' exit='exit'>
          {icon && <Img media={getIcon(icon as TypeAppIcon)} />}
          <hgroup>
            <h2>{title || TYPE_TRANSLATIONS[lang ?? LOCALE][type]}</h2>
            <p>{message}</p>
          </hgroup>
          {closeNeeded && <button onClick={handleClose} name={t('actions.close') as string} aria-label={CLOSE_NOTIFICATION_TRANSLATIONS[lang ?? LOCALE]}>
            <SvgClose color={getTextColorByType(type)} ariaHidden={true} />
          </button>}
        </DivToastItem>
      }
    </AnimatePresence>
  )
})

const DivToastItem = styled(motion.div) <{ $type: TypeToast }>`
  align-items: center;
  background-color: ${({ $type }) => getBgColorByType($type)};
  border-radius: 6px;
  border: 1px solid ${({ $type }) => $type === 'info' ? 'var(--color-cms-blue)' : 'transparent'};
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, .10), 0px 4px 6px -2px rgba(0, 0, 0, .05);
  color: ${({ $type }) => getTextColorByType($type)};
  display: flex;
  font-size: 12px;
  font-weight: 400;
  justify-content: flex-end;
  max-width: calc(100vw - 32px);
  max-width: calc(100dvw - 32px);
  padding: 13px 16px;
  pointer-events: auto;
  width: 100%;
  width: fit-content;
  z-index: 999;

  ${mq.greaterThan('mobile')} {
    min-width: 356px;
  }

  hgroup {
    align-items: flex-start;
    display: flex;
    gap: 4px;
    flex-direction: column;
    flex-grow: 2;
    justify-content: flex-start;
    width: 100%;
  }

  h2 {
    color: ${({ $type }) => $type === 'info' ? 'var(--color-cms-blue)' : 'inherit'};
    font-weight: 500;
  }

  h2,
  p {
    font-size: 14px;
    line-height: 20px;
    text-align: left;
  }

  svg,
  img {
    height: 16px;
    object-fit: contain;
    transition: transform 300ms ease-out;
    width: 16px;
  }

  button {
    flex-shrink: 0;
    height: 48px;
    margin-left: auto;
    padding: 0 16px;

    &:hover {
      svg {
        transform: scale(1.2);
      }
    }
  }
`

const variants = {
  initial: {
    opacity: 0,
    x: '100%',
    transition: { duration: .4 }
  },
  animate: {
    opacity: 1,
    x: '0%',
    transition: { duration: .4 }
  },
  exit: {
    opacity: 0,
    x: '100%',
    transition: { duration: .3 }
  }
}

export const ToastWrapper = styled.aside`
  align-items: flex-end;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-end;
  left: 0;
  max-width: 100vw;
  max-width: 100dvw;
  padding: 16px;
  pointer-events: none;
  position: fixed;
  right: 0;
  transition: 300ms all linear;
  user-select: none;
  width: 100%;
  z-index: 500;

  > div {
    &:last-child {
      margin-bottom: 0;
    }
  }
`


const CLOSE_NOTIFICATION_TRANSLATIONS = {
  en: 'Close notification',
  es: 'Cerrar notificación',
  ca: 'Tancar notificació',
  fr: 'Fermer la notification',
  pt: 'Fechar notificação',
  de: 'Benachrichtigung schließen',
  it: 'Chiudi notifica',
  pl: 'Zamknij powiadomienie',
  ru: 'Закрыть уведомление',
  ja: '通知を閉じる',
  zh: '关闭通知',
  ko: '알림 닫기',
  nl: 'Sluit melding',
  da: 'Luk notifikation',
  sv: 'Stäng avisering',
  no: 'Lukk varsel',
  fi: 'Sulje ilmoitus',
  tr: 'Bildirimi kapat',
  el: 'Κλείστε την ειδοποίηση',
  cs: 'Zavřít oznámení',
  hu: 'Értesítés bezárása',
  ar: 'إغلاق الإشعار'
} as const

const TYPE_TRANSLATIONS = {
  es: {
    info: 'Info',
    success: 'Info', // Reuses 'info' for success in Spanish
    warning: 'Advertencia',
    error: 'Error',
  },
  en: {
    info: 'Information',
    success: 'Success',
    warning: 'Warning',
    error: 'Error',
  },
  ca: {
    info: 'Informació',
    success: 'Informació',
    warning: 'Advertència',
    error: 'Error',
  },
  fr: {
    info: 'Information',
    success: 'Information',
    warning: 'Avertissement',
    error: 'Erreur',
  },
  pt: {
    info: 'Informação',
    success: 'Informação',
    warning: 'Aviso',
    error: 'Erro',
  },
  de: {
    info: 'Information',
    success: 'Information',
    warning: 'Warnung',
    error: 'Fehler',
  },
  it: {
    info: 'Informazione',
    success: 'Informazione',
    warning: 'Avviso',
    error: 'Errore',
  },
  pl: {
    info: 'Informacja',
    success: 'Informacja',
    warning: 'Ostrzeżenie',
    error: 'Błąd',
  },
  ru: {
    info: 'Информация',
    success: 'Информация',
    warning: 'Предупреждение',
    error: 'Ошибка',
  },
  ja: {
    info: '情報',
    success: '情報',
    warning: '警告',
    error: 'エラー',
  },
  zh: {
    info: '信息',
    success: '信息',
    warning: '警告',
    error: '错误',
  },
  ko: {
    info: '정보',
    success: '정보',
    warning: '경고',
    error: '오류',
  },
  nl: {
    info: 'Informatie',
    success: 'Informatie',
    warning: 'Waarschuwing',
    error: 'Fout',
  },
  da: {
    info: 'Information',
    success: 'Information',
    warning: 'Advarsel',
    error: 'Fejl',
  },
  sv: {
    info: 'Information',
    success: 'Information',
    warning: 'Varning',
    error: 'Fel',
  },
  no: {
    info: 'Informasjon',
    success: 'Informasjon',
    warning: 'Advarsel',
    error: 'Feil',
  },
  fi: {
    info: 'Tiedot',
    success: 'Tiedot',
    warning: 'Varoitus',
    error: 'Virhe',
  },
  tr: {
    info: 'Bilgi',
    success: 'Bilgi',
    warning: 'Uyarı',
    error: 'Hata',
  },
  el: {
    info: 'Πληροφορίες',
    success: 'Πληροφορίες',
    warning: 'Προειδοποίηση',
    error: 'Σφάλμα',
  },
  cs: {
    info: 'Informace',
    success: 'Informace',
    warning: 'Upozornění',
    error: 'Chyba',
  },
  hu: {
    info: 'Információ',
    success: 'Információ',
    warning: 'Figyelmeztetés',
    error: 'Hiba',
  },
  ar: {
    info: 'معلومات',
    success: 'معلومات',
    warning: 'تحذير',
    error: 'خطأ',
  }
} as const
